import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { atcb_action } from 'add-to-calendar-button';
import { useAuth } from '../contexts/AuthContext';
import { Enrollment, Schedule, Course, Subscription } from '../types';
import { usePageTitle } from '../hooks/usePageTitle';
import { formatDate, parseSessionTime } from '../utils/dateUtils';
import { useAuthFetch } from '../hooks/useAuthFetch';

const Dashboard: React.FC = () => {
  usePageTitle('My Dashboard');
  const { user } = useAuth();
  const authFetch = useAuthFetch();
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [schedules, setSchedules] = useState<Record<string, Schedule>>({});
  const [courses, setCourses] = useState<Record<string, Course>>({});
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingBanner, setPendingBanner] = useState<{ subscriptionId: string; tier: string } | null>(null);
  const [downloadingCertId, setDownloadingCertId] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const lowerEmail = user?.email?.toLowerCase() || '';
        const [enrollRes, courseRes, scheduleRes, subRes] = await Promise.all([
          authFetch('/api/enrollments'),
          fetch('/api/courses'),
          fetch('/api/schedules'),
          authFetch('/api/subscriptions'),
        ]);

        if (enrollRes.ok) {
          const allEnrollments: Enrollment[] = await enrollRes.json();
          setEnrollments(allEnrollments.filter(
            (e) => e.studentEmail.toLowerCase() === lowerEmail
          ));
        }
        if (courseRes.ok) {
          const courseList: Course[] = await courseRes.json();
          const map: Record<string, Course> = {};
          courseList.forEach((c) => (map[c.id] = c));
          setCourses(map);
        }
        if (scheduleRes.ok) {
          const scheduleList: Schedule[] = await scheduleRes.json();
          const map: Record<string, Schedule> = {};
          scheduleList.forEach((s) => (map[s.id] = s));
          setSchedules(map);
        }

        let subs: Subscription[] = [];
        if (subRes.ok) {
          const allSubs: Subscription[] = await subRes.json();
          subs = allSubs.filter((s) =>
            s.customerEmail.toLowerCase() === lowerEmail ||
            s.studentList?.some((e) => e.toLowerCase() === lowerEmail) ||
            s.coordinatorEmail?.toLowerCase() === lowerEmail ||
            s.teamMembers?.some((m) => m.email.toLowerCase() === lowerEmail)
          );
          setSubscriptions(subs);
        }

        // Check for pending credit card subscription (stored before QuickBooks redirect)
        const pendingStr = localStorage.getItem('pendingSubscription');
        if (pendingStr) {
          try {
            const pending = JSON.parse(pendingStr) as { subscriptionId: string; tier: string };
            const matchingSub = subs.find((s) => s.id === pending.subscriptionId);
            if (matchingSub && matchingSub.status === 'active') {
              // Subscription is now active — show banner
              setPendingBanner(pending);
              localStorage.removeItem('pendingSubscription');
            } else if (matchingSub && matchingSub.status === 'payment_complete_pending_account') {
              // Still pending — keep in localStorage for next visit
              setPendingBanner(null);
            } else {
              // Subscription not found or cancelled — clean up
              localStorage.removeItem('pendingSubscription');
            }
          } catch {
            localStorage.removeItem('pendingSubscription');
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.email) {
      fetchData();
    }
  }, [user, authFetch]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  const activeEnrollments = enrollments.filter((e) => e.status === 'active');
  const completedEnrollments = enrollments.filter((e) => e.status === 'completed');
  const transcriptEnrollments = enrollments
    .filter((e) => e.status === 'completed' || e.status === 'cancelled' || e.status === 'absent')
    .sort((a, b) => {
      const aDate = schedules[a.scheduleId]?.sessionDate || a.enrollmentDate;
      const bDate = schedules[b.scheduleId]?.sessionDate || b.enrollmentDate;
      return bDate.localeCompare(aDate);
    });
  const activeSubscriptions = subscriptions.filter((s) => s.status === 'active');
  const pendingSubscriptions = subscriptions.filter((s) => s.status === 'payment_complete_pending_account');
  const userEmail = user?.email?.toLowerCase() || '';

  const handleAddToCalendar = (
    event: React.MouseEvent<HTMLButtonElement>,
    schedule: Schedule,
    course: Course | null
  ) => {
    const start = parseSessionTime(schedule.startTime);
    const end = parseSessionTime(schedule.endTime);
    const descriptionParts = [
      course?.shortDescription,
      schedule.webinarJoinUrl ? `Join Teams Webinar: ${schedule.webinarJoinUrl}` : null,
    ].filter(Boolean);
    atcb_action(
      {
        name: course?.title || 'Patriot Academy Session',
        description: descriptionParts.join('\n\n') || 'Patriot Academy Premium training session.',
        startDate: schedule.sessionDate,
        endDate: schedule.sessionDate,
        startTime: start.time,
        endTime: end.time,
        timeZone: start.timeZone,
        location: schedule.webinarJoinUrl || 'Microsoft Teams (link provided closer to session)',
        organizer: 'Patriot Academy|training@patriotconsultingtech.com',
        options: ['Apple', 'Google', 'iCal', 'Microsoft365', 'Outlook.com'],
        listStyle: 'modal',
        iCalFileName: `patriot-academy-${schedule.id}`,
      },
      event.currentTarget
    );
  };

  const handleDownloadCertificate = async (
    enrollmentId: string,
    courseTitle: string
  ) => {
    if (downloadingCertId) return;
    setDownloadingCertId(enrollmentId);
    try {
      const res = await authFetch(`/api/enrollments/${enrollmentId}/certificate`);
      if (!res.ok) {
        const message = res.status === 403
          ? 'You do not have permission to download this certificate.'
          : 'We could not generate your certificate. Please try again in a moment.';
        alert(message);
        return;
      }
      const blob = await res.blob();
      const slug = (courseTitle || 'course')
        .replace(/[^a-z0-9]+/gi, '-')
        .toLowerCase();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate-${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download certificate:', err);
      alert('We could not generate your certificate. Please try again in a moment.');
    } finally {
      setDownloadingCertId(null);
    }
  };

  // Identify team subscriptions where user is coordinator
  const coordinatorSubs = activeSubscriptions.filter(
    (s) => s.tier === 'Team' && s.coordinatorEmail?.toLowerCase() === userEmail
  );

  // Identify team subscriptions where user is a non-coordinator team member
  const teamMemberSubs = activeSubscriptions.filter(
    (s) =>
      s.tier === 'Team' &&
      s.coordinatorEmail?.toLowerCase() !== userEmail &&
      s.teamMembers?.some((m) => m.email.toLowerCase() === userEmail)
  );

  // Individual subscriptions (Starter/Pro, or Team subs without enrollment yet)
  const individualSubs = activeSubscriptions.filter(
    (s) => s.tier !== 'Team'
  );

  // Subs without any enrollments (need session selection)
  const subsNeedingSessionPick = individualSubs.filter((sub) => {
    return !enrollments.some((e) => e.subscriptionId === sub.id && e.status === 'active');
  });

  return (
    <div className="dashboard-page">
      <section className="dashboard-header">
        <div className="section-container">
          <h1 className="page-title">Welcome back, {user?.name || 'Student'}</h1>
          <p className="page-subtitle">Your learning dashboard</p>
        </div>
      </section>

      {/* Credit card return banner */}
      {pendingBanner && (
        <section className="section">
          <div className="section-container">
            <div
              style={{
                padding: '1rem 1.5rem',
                background: '#d4edda',
                border: '1px solid #c3e6cb',
                borderRadius: '8px',
                color: '#155724',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem',
              }}
            >
              <div>
                <strong>Payment confirmed!</strong> Your subscription is now active.
              </div>
              {pendingBanner.tier === 'Team' ? (
                <Link to={`/team-setup/${pendingBanner.subscriptionId}`} className="btn btn-primary btn-sm">
                  Set Up Your Team
                </Link>
              ) : (
                <Link to={`/choose-session/${pendingBanner.subscriptionId}`} className="btn btn-primary btn-sm">
                  Choose Your Session
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Prompt for subscriptions that need session selection */}
      {!pendingBanner && subsNeedingSessionPick.length > 0 && (
        <section className="section">
          <div className="section-container">
            {subsNeedingSessionPick.map((sub) => (
              <div
                key={sub.id}
                style={{
                  padding: '1rem 1.5rem',
                  background: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '8px',
                  color: '#856404',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '1rem',
                  marginBottom: '0.75rem',
                }}
              >
                <div>
                  Your <strong>{sub.tier}</strong> subscription is active but you haven&apos;t selected a session yet.
                </div>
                <Link to={`/choose-session/${sub.id}`} className="btn btn-primary btn-sm">
                  Choose Your Session
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Stats */}
      <section className="section">
        <div className="section-container">
          <div className="dashboard-stats">
            <div className="dashboard-stat-card">
              <span className="dashboard-stat-number">{pendingSubscriptions.length}</span>
              <span className="dashboard-stat-label">Pending Orders</span>
              <span style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.25rem', display: 'block', textAlign: 'center' }}>
                Please allow 1 to 2 business days for order processing
              </span>
            </div>
            <div className="dashboard-stat-card">
              <span className="dashboard-stat-number">{activeEnrollments.length}</span>
              <span className="dashboard-stat-label">Active Enrollments</span>
            </div>
            <div className="dashboard-stat-card">
              <span className="dashboard-stat-number">{completedEnrollments.length}</span>
              <span className="dashboard-stat-label">Completed Courses</span>
            </div>
            <div className="dashboard-stat-card">
              <span className="dashboard-stat-number">{activeSubscriptions.length}</span>
              <span className="dashboard-stat-label">Active Subscriptions</span>
            </div>
          </div>
        </div>
      </section>

      {/* Team Coordinator Section */}
      {coordinatorSubs.length > 0 && (
        <section className="section" style={{ background: '#f0f7ff' }}>
          <div className="section-container">
            <h2 className="section-title">Your Teams</h2>
            <div className="subscriptions-list">
              {coordinatorSubs.map((sub) => {
                const memberCount = sub.teamMembers?.length || 0;
                const invitedCount = sub.teamMembers?.filter(
                  (m) => m.inviteStatus === 'sent' || m.inviteStatus === 'accepted'
                ).length || 0;

                return (
                  <div key={sub.id} className="subscription-card" style={{ borderLeft: '4px solid #2563eb' }}>
                    <div className="subscription-header">
                      <span className="subscription-tier">Team - {sub.companyName || 'Your Team'}</span>
                      <span className="status-badge status-active">Coordinator</span>
                    </div>
                    <div className="subscription-details">
                      <div className="subscription-detail">
                        <span className="detail-label">Seats</span>
                        <span className="detail-value">{memberCount} / 10</span>
                      </div>
                      <div className="subscription-detail">
                        <span className="detail-label">Invites Sent</span>
                        <span className="detail-value">{invitedCount}</span>
                      </div>
                      <div className="subscription-detail">
                        <span className="detail-label">Expires</span>
                        <span className="detail-value">{formatDate(sub.expirationDate)}</span>
                      </div>
                    </div>
                    <div style={{ marginTop: '1rem' }}>
                      <Link to={`/team-setup/${sub.id}`} className="btn btn-primary btn-sm">
                        Manage Team
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Team Student Info Section */}
      {teamMemberSubs.length > 0 && (
        <section className="section" style={{ background: '#f0f7ff' }}>
          <div className="section-container">
            <h2 className="section-title">Team Membership</h2>
            {teamMemberSubs.map((sub) => (
              <div
                key={sub.id}
                className="subscription-card"
                style={{ marginBottom: '1rem', borderLeft: '4px solid #10b981' }}
              >
                <div className="subscription-header">
                  <span className="subscription-tier">{sub.companyName || 'Team Plan'}</span>
                  <span className="status-badge status-active">Member</span>
                </div>
                <div className="subscription-details">
                  <div className="subscription-detail">
                    <span className="detail-label">Coordinator</span>
                    <span className="detail-value">{sub.coordinatorName || 'N/A'}</span>
                  </div>
                  <div className="subscription-detail">
                    <span className="detail-label">Expires</span>
                    <span className="detail-value">{formatDate(sub.expirationDate)}</span>
                  </div>
                </div>
                {activeEnrollments.filter((e) => e.subscriptionId === sub.id).length === 0 && (
                  <p style={{ marginTop: '0.75rem', color: '#6b7280', fontSize: '0.9rem' }}>
                    Your coordinator {sub.coordinatorName} is setting up your sessions.
                    You&apos;ll receive an email when your session is assigned.
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Upcoming Sessions */}
      <section className="section">
        <div className="section-container">
          <h2 className="section-title">Upcoming Sessions</h2>
          {activeEnrollments.length > 0 ? (
            <div className="dashboard-enrollments">
              {activeEnrollments.map((enrollment) => {
                const schedule = schedules[enrollment.scheduleId];
                const course = schedule ? courses[schedule.courseId] : null;
                return (
                  <div key={enrollment.id} className="dashboard-enrollment-card">
                    <div className="enrollment-info">
                      <h3 className="enrollment-course-title">
                        {course?.title || 'Course'}
                      </h3>
                      {schedule && (
                        <div className="enrollment-schedule">
                          <span className="enrollment-date">
                            {formatDate(schedule.sessionDate, {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </span>
                          <span className="enrollment-time">
                            {schedule.startTime} - {schedule.endTime}
                          </span>
                        </div>
                      )}
                      <span className={`status-badge status-${enrollment.status}`}>
                        {enrollment.status}
                      </span>
                    </div>
                    <div className="enrollment-card-actions">
                      {schedule?.webinarJoinUrl && (
                        <a
                          href={schedule.webinarJoinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary btn-sm"
                        >
                          Join Teams Webinar
                        </a>
                      )}
                      {schedule?.recordingUrl && (
                        <a
                          href={schedule.recordingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline btn-sm"
                        >
                          Watch Recording
                        </a>
                      )}
                      {schedule && (
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          onClick={(e) => handleAddToCalendar(e, schedule, course)}
                        >
                          Add to Calendar
                        </button>
                      )}
                      {course && (
                        <Link to={`/courses/${course.id}`} className="btn btn-outline btn-sm">
                          View Course
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <p>No upcoming sessions. Browse our course catalog to enroll.</p>
              <Link to="/courses" className="btn btn-primary">
                Browse Courses
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Subscriptions */}
      {activeSubscriptions.length > 0 && (
        <section className="section section-alt">
          <div className="section-container">
            <h2 className="section-title">Your Subscriptions</h2>
            <div className="subscriptions-list">
              {activeSubscriptions.map((sub) => (
                <div key={sub.id} className="subscription-card">
                  <div className="subscription-header">
                    <span className="subscription-tier">{sub.tier}</span>
                    <span className={`status-badge status-${sub.status}`}>{sub.status}</span>
                  </div>
                  <div className="subscription-details">
                    <div className="subscription-detail">
                      <span className="detail-label">Purchased</span>
                      <span className="detail-value">
                        {formatDate(sub.purchaseDate)}
                      </span>
                    </div>
                    <div className="subscription-detail">
                      <span className="detail-label">Expires</span>
                      <span className="detail-value">
                        {formatDate(sub.expirationDate)}
                      </span>
                    </div>
                    {sub.coursesRemaining !== null && (
                      <div className="subscription-detail">
                        <span className="detail-label">Courses Remaining</span>
                        <span className="detail-value">{sub.coursesRemaining}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Transcript */}
      {transcriptEnrollments.length > 0 && (
        <section className="section">
          <div className="section-container">
            <h2 className="section-title">Transcript</h2>
            <div className="table-responsive">
              <table className="transcript-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #e2e8f0' }}>Course</th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #e2e8f0' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #e2e8f0' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transcriptEnrollments.map((enrollment) => {
                    const schedule = schedules[enrollment.scheduleId];
                    const course = schedule ? courses[schedule.courseId] : null;
                    const sessionDate = schedule?.sessionDate || enrollment.enrollmentDate;
                    return (
                      <tr key={enrollment.id}>
                        <td style={{ padding: '12px 8px', borderBottom: '1px solid #f1f5f9' }}>
                          {course?.title || 'Course'}
                        </td>
                        <td style={{ padding: '12px 8px', borderBottom: '1px solid #f1f5f9' }}>
                          {formatDate(sessionDate)}
                        </td>
                        <td style={{ padding: '12px 8px', borderBottom: '1px solid #f1f5f9' }}>
                          <span className={`status-badge status-${enrollment.status}`}>
                            {enrollment.status}
                          </span>
                        </td>
                        <td style={{ padding: '12px 8px', borderBottom: '1px solid #f1f5f9' }}>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {enrollment.status === 'completed' && (
                              <button
                                type="button"
                                className="btn btn-outline btn-sm"
                                onClick={() =>
                                  handleDownloadCertificate(enrollment.id, course?.title || 'course')
                                }
                                disabled={downloadingCertId === enrollment.id}
                              >
                                {downloadingCertId === enrollment.id
                                  ? 'Preparing…'
                                  : 'Download Certificate'}
                              </button>
                            )}
                            {schedule?.recordingUrl &&
                              (enrollment.status === 'completed' || enrollment.status === 'absent') && (
                                <a
                                  href={schedule.recordingUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-outline btn-sm"
                                >
                                  Watch Recording
                                </a>
                              )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Dashboard;
